import React, { useState, useRef, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AddIcon from '@mui/icons-material/Add'
import { TppSection, TppRow } from '../../types'
import RowListItem from './RowListItem'

interface SectionListItemProps {
    section: TppSection
    rows: TppRow[]
    onUpdateSectionTitle: (sectionId: string, title: string) => Promise<void>
    onDeleteSection: (section: TppSection) => void
    onAddRow: (sectionId: string) => void
    onUpdateRowTitle: (rowId: string, title: string) => Promise<void>
    onDeleteRow: (row: TppRow) => void
    isUpdatingSection: boolean
    isUpdatingRow: boolean
    isAddingRow: boolean
}

const SectionListItem = ({
    section,
    rows,
    onUpdateSectionTitle,
    onDeleteSection,
    onAddRow,
    onUpdateRowTitle,
    onDeleteRow,
    isUpdatingSection,
    isUpdatingRow,
    isAddingRow,
}: SectionListItemProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(section.title)
    const [isExpanded, setIsExpanded] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    const handleStartEdit = () => {
        setEditValue(section.title)
        setIsEditing(true)
    }

    const handleCancelEdit = () => {
        setEditValue(section.title)
        setIsEditing(false)
    }

    const handleSaveEdit = async () => {
        if (editValue.trim() && editValue !== section.title) {
            await onUpdateSectionTitle(section._id, editValue.trim())
        }
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveEdit()
        } else if (e.key === 'Escape') {
            handleCancelEdit()
        }
    }

    const sectionRows = rows.filter((r) => section.rowIds.includes(r._id))

    return (
        <Stack>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={1.5}
                px={2}
                sx={{
                    borderRadius: '8px',
                    bgcolor: 'figma.grayscale.tier6',
                    '&:hover': {
                        bgcolor: 'figma.grayscale.tier5',
                    },
                }}
            >
                {isEditing ? (
                    <Stack direction="row" alignItems="center" gap={1} flex={1}>
                        <TextField
                            inputRef={inputRef}
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isUpdatingSection}
                            sx={{ flex: 1 }}
                        />
                        <IconButton
                            size="small"
                            onClick={handleSaveEdit}
                            disabled={isUpdatingSection || !editValue.trim()}
                        >
                            {isUpdatingSection ? (
                                <CircularProgress size={18} />
                            ) : (
                                <CheckIcon fontSize="small" color="success" />
                            )}
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit} disabled={isUpdatingSection}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Typography variant="16x600" color="figma.grayscale.textHier1">
                                {section.title}
                            </Typography>
                            <Typography variant="14x400" color="figma.grayscale.textHier3">
                                ({sectionRows.length} rows)
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={0.5}>
                            <IconButton size="small" onClick={handleStartEdit} sx={{ color: 'figma.grayscale.tier2' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => onDeleteSection(section)}
                                sx={{ color: 'figma.grayscale.tier2', '&:hover': { color: 'error.main' } }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </>
                )}
            </Stack>

            <Collapse in={isExpanded}>
                <Stack py={1} gap={0.5}>
                    {sectionRows.map((row) => (
                        <RowListItem
                            key={row._id}
                            row={row}
                            onUpdateTitle={onUpdateRowTitle}
                            onDelete={onDeleteRow}
                            isUpdating={isUpdatingRow}
                        />
                    ))}
                    <Button
                        startIcon={isAddingRow ? <CircularProgress size={16} /> : <AddIcon />}
                        onClick={() => onAddRow(section._id)}
                        disabled={isAddingRow}
                        sx={{
                            ml: 4,
                            justifyContent: 'flex-start',
                            color: 'figma.grayscale.textHier3',
                            '&:hover': {
                                color: 'figma.primary',
                                bgcolor: 'transparent',
                            },
                        }}
                    >
                        Add Row
                    </Button>
                </Stack>
            </Collapse>
        </Stack>
    )
}

export default SectionListItem
